<template>
  <section
    class="poola-last-run"
    :class="{ 'poola-last-run--compact': compact }"
    :aria-label="t('poola.lastRun.title')"
  >
    <!--
      PoolaLastRunCard — the recap of the LAST finished Poola, shown on the countdown
      screen (and, full, on an empty countdown) so the next Poola is never a blank
      wait. A CONTENT FRAGMENT: no fetch, no timer, no store, never its own
      <f7-page>. It renders ONLY what usePoolaDeal.previousRun carries (the numbers
      and up to 6 completed offers ride on GET /poola-deals/current's meta) — it must
      NOT use usePoolaOffers, whose full load flushes the whole PoolaOffer repo and
      would empty the upcoming-offers list on the same screen.

      compact → title + the two numbers + link (no swipe row), used above a list of
      upcoming offers so old and new products never sit side by side.
    -->

    <div class="poola-last-run__head">
      <h3 class="poola-last-run__title">{{ t('poola.lastRun.title') }}</h3>
      <span v-if="closedIn" class="poola-last-run__closed">{{ closedIn }}</span>
    </div>

    <p v-if="visitor" class="poola-last-run__visitor">{{ t('poola.lastRun.visitorLine') }}</p>

    <!-- Two numbers. A value that is null or 0 hides its tile — never a hollow 0. -->
    <div
      v-if="tiles.length"
      class="poola-last-run__tiles"
      :class="{ 'poola-last-run__tiles--single': tiles.length === 1 }"
    >
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="poola-last-run__tile"
        :class="`poola-last-run__tile--${tile.key}`"
        :data-tile="tile.key"
      >
        <div class="poola-last-run__number">
          <span class="poola-last-run__amount">{{ tile.amount }}</span>
          <span class="poola-last-run__currency">{{ t('poola.festival.finale.currency') }}</span>
        </div>
        <div class="poola-last-run__tile-label">{{ tile.label }}</div>
      </div>
    </div>

    <!-- Swipe row: plain CSS horizontal scroll (NOT a swiper). overflow-y is
         hidden so a vertical drag on the row still scrolls the page and pulls to
         refresh; only the horizontal axis is captured. -->
    <div v-if="showRow" class="poola-last-run__row" role="list">
      <div
        v-for="(item, index) in highlights"
        :key="itemKey(item, index)"
        class="poola-last-run__item"
        role="listitem"
      >
        <div class="poola-last-run__media">
          <!-- No photo (or one that failed to load) → the same placeholder
               PoolaOfferCard shows. -->
          <img
            v-if="item.photoUrl && !failedPhotos[itemKey(item, index)]"
            :src="item.photoUrl"
            :alt="item.displayTitle"
            class="poola-last-run__img"
            loading="lazy"
            @error="failedPhotos[itemKey(item, index)] = true"
          />
          <f7-icon
            v-else
            ios="f7:bag_fill"
            md="material:shopping_bag"
            size="30"
            color="white"
          ></f7-icon>

          <span
            v-if="sharePct(item) > 0"
            class="poola-last-run__badge poola-last-run__badge--share"
          >
            {{ t('poola.buy.card.shareBadge', { pct: sharePct(item) }) }}
          </span>
          <span class="poola-last-run__badge poola-last-run__badge--done">
            {{ t('poola.buy.card.completedBadge') }}
          </span>
        </div>

        <p class="poola-last-run__item-title">{{ item.displayTitle }}</p>

        <p v-if="item.cashCents != null" class="poola-last-run__price">
          <span class="poola-last-run__cash">{{ formatCurrency(item.cashCents) }}</span>
          <span v-if="item.unitPriceCents != null" class="poola-last-run__instead">
            {{ t('poola.buy.card.insteadOf') }} <s>{{ formatCurrency(item.unitPriceCents) }}</s>
          </span>
        </p>
      </div>
    </div>

    <!-- The link only exists once a destination does (the last-Poola page). -->
    <f7-link v-if="seeAllHref" :href="seeAllHref" class="poola-last-run__link">
      <span>{{ t('poola.lastRun.seeAll') }}</span>
      <svg
        class="poola-last-run__chevron"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M9 6l6 6-6 6" />
      </svg>
    </f7-link>
  </section>
</template>

<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  // usePoolaDeal.previousRun: { id, title, status, startAt, endAt, festival, highlights }.
  run: {
    type: Object,
    required: true,
  },
  // Title + numbers + link only (no swipe row).
  compact: {
    type: Boolean,
    default: false,
  },
  // A prospect (non-member) also gets one invitation line under the header.
  visitor: {
    type: Boolean,
    default: false,
  },
  // Where the link goes (the last-Poola page). null → no link is rendered.
  seeAllHref: {
    type: String,
    default: null,
  },
});

const { t, locale } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();

// ── Numbers: whole EGP, Western digits — the way PoolaPoolHero formats its finale
// numbers (never Arabic-Indic, owner-locked).
const wholeEgp = new Intl.NumberFormat('en-US');
function formatEgp(cents) {
  return wholeEgp.format(Math.round((Number(cents) || 0) / 100));
}

const tiles = computed(() => {
  const festival = props.run?.festival;
  return [
    {
      key: 'profit',
      cents: festival?.collectivePoolshareUsedCents,
      label: t('poola.lastRun.profitTile'),
    },
    {
      key: 'credit',
      cents: festival?.collectivePoolcreditReturnedCents,
      label: t('poola.lastRun.creditTile'),
    },
  ]
    .filter((tile) => Number(tile.cents) > 0)
    .map((tile) => ({ ...tile, amount: formatEgp(tile.cents) }));
});

// ── Subtitle: the month the run closed, in Cairo time (the club's calendar), in
// the active locale. No subtitle when endAt is missing or unparseable.
const CAIRO_TIME_ZONE = 'Africa/Cairo';
const closedIn = computed(() => {
  const raw = props.run?.endAt;
  const ms = typeof raw === 'number' ? raw : Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  try {
    const month = new Intl.DateTimeFormat(locale.value, {
      month: 'long',
      timeZone: CAIRO_TIME_ZONE,
    }).format(ms);
    return t('poola.lastRun.closedIn', { month });
  } catch {
    return null;
  }
});

// ── Swipe row ──────────────────────────────────────────────────────────────────
const highlights = computed(() =>
  Array.isArray(props.run?.highlights) ? props.run.highlights : [],
);
const showRow = computed(() => !props.compact && highlights.value.length > 0);

// A row without an id still needs a stable key.
function itemKey(item, index) {
  return String(item?.id ?? `i${index}`);
}

// A photo URL that fails to load falls back to the placeholder (per tile).
const failedPhotos = reactive({});

// Same rounding as PoolaOfferCard's PoolShare seal.
function sharePct(item) {
  const bp = Number(item?.discountBp);
  return Number.isFinite(bp) ? Math.round(bp / 100) : 0;
}
</script>

<style scoped>
.poola-last-run {
  margin: 0 16px 16px;
  padding: 16px;
  background: var(--poolit-bg-primary);
  border: 1px solid var(--poolit-border-color);
  border-radius: var(--poolit-radius-lg);
  box-shadow: var(--poolit-shadow-soft);
  text-align: start;
}

.poola-last-run--compact {
  margin-bottom: 12px;
  padding: 14px 16px;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.poola-last-run__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
  margin-bottom: 12px;
}

.poola-last-run__title {
  margin: 0;
  color: var(--poolit-text-primary);
  font-size: var(--poolit-font-size-lg);
  font-weight: var(--poolit-font-weight-bold);
  line-height: 1.45;
}

.poola-last-run__closed {
  color: var(--poolit-text-secondary);
  font-size: var(--poolit-font-size-caption);
  line-height: 1.4;
}

.poola-last-run__visitor {
  margin: 0 0 12px;
  color: var(--poolit-text-secondary);
  font-size: var(--poolit-font-size-caption);
  line-height: var(--poolit-line-height-body);
}

/* ── The two numbers ─────────────────────────────────────────────────────── */
.poola-last-run__tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.poola-last-run__tiles--single {
  grid-template-columns: minmax(0, 1fr);
}

.poola-last-run__tile {
  padding: 12px;
  border-radius: var(--poolit-radius-md);
}

.poola-last-run__tile--profit {
  background: var(--poolit-warning-50);
  border: 1px solid var(--poolit-warning-100);
  color: var(--poolit-warning-700);
}

.poola-last-run__tile--credit {
  background: var(--poolit-success-50);
  border: 1px solid var(--poolit-success-100);
  color: var(--poolit-success-700);
}

/* flex-wrap so a seven-figure amount drops its currency under it instead of
   overflowing the tile at 375px. */
.poola-last-run__number {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 4px;
}

.poola-last-run__amount {
  font-size: 1.5rem;
  font-weight: var(--poolit-font-weight-bold);
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
}

.poola-last-run__currency {
  font-size: 0.8125rem;
  font-weight: var(--poolit-font-weight-semibold);
}

.poola-last-run__tile-label {
  margin-top: 4px;
  color: var(--poolit-text-secondary);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.poola-last-run--compact .poola-last-run__tiles {
  margin-bottom: 12px;
}

.poola-last-run--compact .poola-last-run__tile {
  padding: 10px 12px;
}

/* ── Swipe row ───────────────────────────────────────────────────────────── */
/* Bleeds to the card edges (negative margin) and keeps the card's own padding
   inside the scroller. overflow-y:hidden leaves vertical drags to the page;
   overscroll-behavior-x stops the sideways edge from chaining into the
   browser's swipe-back. No snap, no JS. */
.poola-last-run__row {
  display: flex;
  gap: 10px;
  margin: 0 -16px 12px;
  padding: 0 16px 4px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}

.poola-last-run__row::-webkit-scrollbar {
  display: none;
}

.poola-last-run__item {
  flex: 0 0 116px;
  width: 116px;
  min-width: 0;
}

.poola-last-run__media {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 116px;
  overflow: hidden;
  border-radius: var(--poolit-radius-md);
  /* The placeholder PoolaOfferCard uses when an offer has no photo. */
  background: linear-gradient(135deg, var(--poolit-primary-700), var(--poolit-primary-900));
}

.poola-last-run__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.poola-last-run__badge {
  position: absolute;
  inset-inline-start: 6px;
  padding: 2px 8px;
  border-radius: var(--poolit-radius-full);
  color: #fff;
  font-size: 11px;
  font-weight: var(--poolit-font-weight-bold);
  line-height: 18px;
  white-space: nowrap;
}

.poola-last-run__badge--share {
  top: 6px;
  background: var(--poolit-primary-600);
}

.poola-last-run__badge--done {
  bottom: 6px;
  background: var(--poolit-success-700);
}

.poola-last-run__item-title {
  display: -webkit-box;
  height: 36px;
  margin: 8px 0 0;
  overflow: hidden;
  color: var(--poolit-text-primary);
  font-size: 0.8125rem;
  font-weight: var(--poolit-font-weight-semibold);
  line-height: 18px;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.poola-last-run__price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
  margin: 4px 0 0;
}

.poola-last-run__cash {
  color: var(--poolit-text-primary);
  font-size: 0.875rem;
  font-weight: var(--poolit-font-weight-bold);
  line-height: 20px;
}

.poola-last-run__instead {
  color: var(--poolit-text-secondary);
  font-size: 0.75rem;
  line-height: 18px;
}

/* ── Link ────────────────────────────────────────────────────────────────── */
/* ≥44px tall; a real <a href> through f7-link so the Framework7 router opens it. */
.poola-last-run__link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 48px;
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-primary-50);
  color: var(--poolit-primary-800);
  font-size: 0.9375rem;
  font-weight: var(--poolit-font-weight-bold);
  text-decoration: none;
}

.poola-last-run__chevron {
  flex: none;
}
</style>

<!-- Non-scoped block: rules that must reach [dir='rtl'] on <html>.
     Per CLAUDE.md: never :global(ancestor) .descendant inside <style scoped>.
     Same two RTL rules PoolaPoolHero's finale numbers rely on, plus the chevron
     (drawn pointing to the inline end: right in LTR, left in RTL). -->
<style>
[dir='rtl'] .poola-last-run__number {
  flex-direction: row-reverse;
}

[dir='rtl'] .poola-last-run__chevron {
  transform: scaleX(-1);
}
</style>
